import { Alert } from '@design-system';
import { isReleaseTypeBlockchainAuction } from '@utils/product';
import { storyblokProductReleaseType } from '@utils/storyblokReleaseType';
import { useTranslation } from 'next-i18next';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import {
  presentedPurchasingState,
  purchasingState,
} from '../../../helpers/purchasing';
import { useCountryCode } from '../../../hooks/useCountryCode';
import {
  ProductStoryblok,
  ProductV2Storyblok,
} from '../../../types/generated-storyblok';
import type { ProductPurchaseDetails } from '../../../types/Purchasing';
import { AuctionForm } from '../../Auction/Manifold/AuctionForm';
import { CrossSellContextProvider } from '../../Containers/CrossSell/CrossSellContext';
import PurchaseForm from '../../Containers/PurchaseForm';
import { DrawCta } from '../../Containers/PurchaseForm/ctas/Draw';
import { SubscribeCta } from '../../Containers/PurchaseForm/ctas/Subscribe';
import { PurchaseFormCard } from '../../Containers/PurchaseForm/PurchaseFormCard';
import CollectionSetForm from '../../Containers/ReviewCheckoutDrawer/CollectionSetForm';
import Drawer from '../../Layout/Drawer';
import type { ProductDetailsProps } from './types';

export const ProductCTA = forwardRef(
  <T extends ProductPurchaseDetails>(
    { layout = 'drawer', product, purchasing }: ProductDetailsProps<T>,
    ref: React.Ref<{ openCheckoutDrawer: null | (() => void) }>,
  ) => {
    const [collectoinSetDrawerShow, setCollectionSetDrawerShow] =
      useState(false);
    const [countryCode] = useCountryCode();
    const { t } = useTranslation('product');
    const { t: tCountries } = useTranslation('countries');
    const blockChainAuction = product.nftAuction?.[0];

    const releaseType = storyblokProductReleaseType(product);

    const { product: shopifyProduct } = purchasing;

    useEffect(() => {
      // if there's a pre-selected collection set, show the releases form
      if (purchasing.isPreSelectedCollectionSet && !collectoinSetDrawerShow) {
        setCollectionSetDrawerShow(true);
        /**
         * Showing the releases from with pre-selected options only needed when user lands on the page.
         * We have to make sure form doesn't re-open once it was closed
         */
        purchasing.setIsPreSelectedCollectionSet(false);
      }
    }, [collectoinSetDrawerShow, purchasing]);

    const state = shopifyProduct
      ? presentedPurchasingState(
          purchasingState({
            releaseType: product.releaseType,
            closingDate: product.closingDate,
            available: purchasing?.product?.available,
            tags: purchasing?.product?.tags,
          }),
        )
      : '';
    const availableToBuy = state === 'AVAILABLE';
    const availableToSubscribe = [
      'COMING_SOON',
      'SOLD_OUT',
      'ENQUIRY',
    ].includes(state);
    const availableForBlockchainAuction =
      state === 'AVAILABLE' &&
      isReleaseTypeBlockchainAuction(product.releaseType);

    const notAvailableInYourRegion =
      shopifyProduct?.excludeFromRegions.includes(countryCode);

    const purchaseFormOnBuyNowClickMethodRefRef = useRef<{
      onBuyNowClick: () => void;
    }>(null);

    useImperativeHandle(ref, () => {
      // when subscribe is available
      if (shopifyProduct && availableToSubscribe) {
        return {
          openCheckoutDrawer: () => setCollectionSetDrawerShow(true),
        };
      }

      // when buy now is available
      if (!availableForBlockchainAuction && availableToBuy) {
        return {
          openCheckoutDrawer: () =>
            purchaseFormOnBuyNowClickMethodRefRef.current?.onBuyNowClick(),
        };
      }
      return {
        openCheckoutDrawer: null,
      };
    });

    if (product.id === undefined) {
      return null;
    }

    if (notAvailableInYourRegion) {
      return (
        <div className="mb-3">
          <Alert status="info">
            {t('notAvailableForSaleInRegion', {
              country: tCountries(countryCode),
            })}
          </Alert>
        </div>
      );
    }

    return (
      <>
        <PurchaseFormCard>
          <CrossSellContextProvider
            product={
              product as unknown as ProductStoryblok | ProductV2Storyblok
            }
          >
            {shopifyProduct && availableToSubscribe && (
              <SubscribeCta
                product={product}
                purchasingController={purchasing}
                state={state}
              />
            )}
            {state === 'COMING_SOON' &&
              (releaseType === 'draw' || releaseType === 'auction') && (
                <DrawCta product={product} purchasingController={purchasing} />
              )}
            {availableForBlockchainAuction &&
            blockChainAuction?.manifoldListingId ? (
              <AuctionForm
                listingId={blockChainAuction.manifoldListingId}
                ranked={product.releaseType === 'nft_ranked_auction'}
              />
            ) : null}
            {!availableForBlockchainAuction && availableToBuy ? (
              <PurchaseForm
                ref={purchaseFormOnBuyNowClickMethodRefRef}
                layout={layout}
                product={product}
                purchasingController={purchasing}
              />
            ) : null}
          </CrossSellContextProvider>
        </PurchaseFormCard>

        {shopifyProduct &&
        availableToSubscribe &&
        purchasing.collectionSet &&
        purchasing.collectionSet.length > 1 ? (
          <Drawer
            onClose={() => {
              setCollectionSetDrawerShow(false);
              // Closing leaves the page as it was before "Enter draw for set".
              purchasing.collectionSetRestore?.();
            }}
            shown={collectoinSetDrawerShow}
            title={t('drawers.review.releases.title')}
          >
            <div className="mx-auto w-full max-w-md">
              <CollectionSetForm
                description={purchasing.collectionSetDescription}
                items={purchasing.collectionSet}
                mode={
                  state === 'COMING_SOON' &&
                  (releaseType === 'draw' || releaseType === 'auction')
                    ? 'draw'
                    : 'selector'
                }
                selectAll={() => purchasing.collectionSetToggleAll()}
                toggleItem={(id) => purchasing.collectionSetToggleId(id)}
              />
            </div>
          </Drawer>
        ) : null}
      </>
    );
  },
);
